import React, {useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Tooltip} from '@mui/material';
import Table from '../../../common/components/ItemTable/v2/Table';
import Check from '../../../assets/icons/Check';
import Cancel from '../../../assets/icons/Cancel';
import _Modal from '../../../common/components/Modal/v2/Modal';
import Loader from '../../../assets/animations/Loader';
import CustomAlert from '../../../common/components/Alert/CustomAlert';
import ListPaginator from '../../../common/components/ListPaginator/ListPaginator';
import actions from '../../../redux/actions';

const GroupedAttentionsList = ({
    groupedAttentions,
    getAllAttentionsState,
    role,
    deleteAttention,
    clearDeleteAttentionState,
    deletedAttentionState,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    handleChangeRowsPerPage,
    pageQuantity,
    listData,
}) => {
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [showModal, setShowModal] = useState(false);
    const [modalButtonAction] = useState(null);
    const [modalInfo, setModalInfo] = useState({});

    const handleClose = () => {
        setShowModal(false);
        clearDeleteAttentionState();
    };

    const toggleGroup = (groupIndex) => {
        console.log('Toggling group:', groupIndex);
        const newExpandedGroups = new Set(expandedGroups);
        if (newExpandedGroups.has(groupIndex)) {
            newExpandedGroups.delete(groupIndex);
        } else {
            newExpandedGroups.add(groupIndex);
        }
        console.log('New expanded groups:', Array.from(newExpandedGroups));
        setExpandedGroups(newExpandedGroups);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
        });
    };

    // Extract data from group for header display
    const getGroupHeaderData = (group) => {
        if (!group.attentions || group.attentions.length === 0) {
            return {
                date: 'N/A',
                protocolCode: 'N/A',
                amount: group.totalPaidAmountFormatted || '$0',
                name: 'N/A',
                nameTooltip: '',
                center: 'N/A',
                address: 'N/A',
            };
        }

        // Use the first attention in the group for header data
        const firstAttention = group.attentions[0];
        const isAffiliateMissing = firstAttention.affiliateFullName === undefined;
        const displayName = isAffiliateMissing ? firstAttention.accountFullName : firstAttention.affiliateFullName;
        const nameTooltip = isAffiliateMissing ?
            `DNI: ${firstAttention.accountDocument || ''}` :
            firstAttention.numbers;

        return {
            date: formatDate(firstAttention.createdDate),
            protocolCode: firstAttention.protocolCode || 'N/A',
            amount: group.totalPaidAmountFormatted || '$0',
            name: displayName || 'N/A',
            nameTooltip,
            center: firstAttention.providerName || 'N/A',
            address: firstAttention.branchAddress || 'N/A',
        };
    };

    React.useEffect(() => {
        if (deletedAttentionState.success || deletedAttentionState.error) {
            const info = deletedAttentionState.success ?
                {title: 'Atención eliminada correctamente', content: <Check height={'44px'} width={'44px'}/>} :
                {
                    title: 'La atención no pudo ser eliminada',
                    text: 'El plazo para realizar esta acción ha expirado',
                    content: <Cancel height={'44px'} width={'44px'} color={'#AB5035'}/>,
                };
            setModalInfo(info);
            setShowModal(true);
        }
    }, [deletedAttentionState]);

    if (getAllAttentionsState.loading) {
        return <Loader width={'80px'} height={'80px'} />;
    }

    if (getAllAttentionsState.error) {
        return <CustomAlert text={'atenciones' || String(getAllAttentionsState.error)} severity='error'/>;
    }

    if (!Array.isArray(groupedAttentions)) {
        return <CustomAlert text={'Error: Datos de atenciones no válidos'} severity='error'/>;
    }

    // Check if there are no grouped attentions
    if (!groupedAttentions || groupedAttentions.length === 0) {
        return <CustomAlert text={'No se encontraron atenciones'} severity='info'/>;
    }

    // Debug: Log the grouped attentions data
    console.log('GroupedAttentionsList received:', groupedAttentions);
    console.log('listData received:', listData);
    if (groupedAttentions.length > 0 && groupedAttentions[0].attentions) {
        console.log('First group attentions:', groupedAttentions[0].attentions);
        console.log('First group attentions length:', groupedAttentions[0].attentions.length);
    }

    return (
        <div className="grouped-attentions-container">
            {groupedAttentions && groupedAttentions.length > 0 && (
                <>
                    {/* Header Row */}
                    <div className="group-header-row">
                        <div className="group-row-content">
                            <div className="group-cell date-cell">
                                <span className="header-label">FECHA</span>
                            </div>
                            <div className="group-cell protocol-cell">
                                <span className="header-label">PROTOCOLO</span>
                            </div>
                            <div className="group-cell amount-cell">
                                <span className="header-label">MONTO TOTAL</span>
                            </div>
                            <div className="group-cell name-cell">
                                <span className="header-label">AFILIADO</span>
                            </div>
                            <div className="group-cell center-cell">
                                <span className="header-label">CENTRO MÉDICO</span>
                            </div>
                            <div className="group-cell address-cell">
                                <span className="header-label">SEDE</span>
                            </div>
                            <div className="group-cell toggle-cell">
                                <span className="header-label"></span>
                            </div>
                        </div>
                    </div>

                    {groupedAttentions.map((group, groupIndex) => {
                        const headerData = getGroupHeaderData(group);
                        return (
                            <div key={groupIndex} className="group-item">
                                {/* Group Header - Styled like table row */}
                                <div
                                    className="group-header-table-row"
                                    onClick={() => {
                                        console.log('Group header clicked for index:', groupIndex);
                                        toggleGroup(groupIndex);
                                    }}
                                    style={{cursor: 'pointer'}}
                                >
                                    <div className="group-row-content">
                                        <div className="group-cell date-cell">
                                            {headerData.date}
                                        </div>
                                        <div className="group-cell protocol-cell">
                                            {headerData.protocolCode}
                                        </div>
                                        <div className="group-cell amount-cell">
                                            {headerData.amount}
                                        </div>
                                        <div className="group-cell name-cell">
                                            <Tooltip title={headerData.nameTooltip}>
                                                <span>{headerData.name}</span>
                                            </Tooltip>
                                        </div>
                                        <div className="group-cell center-cell">
                                            <div className="center-name">{headerData.center}</div>
                                        </div>
                                        <div className="group-cell address-cell">
                                            <div className="address-text">{headerData.address}</div>
                                        </div>
                                        <div className="group-cell toggle-cell">
                                            <div className="group-toggle">
                                                <span className="attention-count" style={{marginRight: '8px'}}>({group.attentions ? group.attentions.length : 0})</span>
                                                {expandedGroups.has(groupIndex) ? '▼' : '▶'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded Table */}
                                {expandedGroups.has(groupIndex) && (
                                    <div className="group-content">
                                        {console.log('Rendering table for group:', groupIndex, 'with attentions:', group.attentions)}
                                        <div
                                            className="grouped-table-container"
                                            style={{
                                                width: '100%',
                                                height: `${Math.max(200, 60 + (group.attentions.length * 60) + 60)}px`,
                                                minHeight: '200px',
                                                maxHeight: '600px',
                                            }}
                                        >
                                            {console.log('Table props:', {
                                                attributes: listData,
                                                rows: group.attentions.map((attention, attIndex) => ({
                                                    ...attention,
                                                    id: attention.attentionId || `attention-${groupIndex}-${attIndex}`,
                                                })),
                                                rowCount: group.attentions.length,
                                            })}

                                            <Table
                                                attributes={listData}
                                                rows={group.attentions.map((attention, attIndex) => ({
                                                    ...attention,
                                                    id: attention.attentionId || `attention-${groupIndex}-${attIndex}`,
                                                    modality: attention.modality || 'N/A', // Ensure modality exists
                                                }))}
                                                noRowsMessage={'No hay atenciones en este grupo'}
                                                rowCount={group.attentions.length}
                                                pageSizeOptions={[10, 25, 50, 100]}
                                                pageSize={group.attentions.length}
                                                currentPage={1}
                                                onPageChange={() => {}}
                                                onPageSizeChange={() => {}}
                                                includeCheckbox={false}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Pagination */}
                    <div className="grouped-pagination-container">
                        <ListPaginator
                            pageQuantity={pageQuantity}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            initInOne={true}
                        />
                    </div>
                </>
            )}

            {/* Delete Modal */}
            <_Modal
                showModal={showModal}
                handleClose={handleClose}
                handleClick={modalButtonAction}
                modalText={modalInfo.text}
                modalContent={modalInfo.content}
                modalTitle={modalInfo.title}
                modalButtonText={modalInfo.buttonText}
            />
        </div>
    );
};

GroupedAttentionsList.propTypes = {
    groupedAttentions: PropTypes.array,
    getAllAttentionsState: PropTypes.object,
    role: PropTypes.string,
    deleteAttention: PropTypes.func,
    clearDeleteAttentionState: PropTypes.func,
    deletedAttentionState: PropTypes.object,
    currentPage: PropTypes.number,
    setCurrentPage: PropTypes.func,
    rowsPerPage: PropTypes.number,
    handleChangeRowsPerPage: PropTypes.func,
    pageQuantity: PropTypes.number,
    listData: PropTypes.array,
};

const mapDispatchToProps = (dispatch) => ({
    deleteAttention: (body) => dispatch(actions.home.deleteAttentionRequest(body)),
    clearDeleteAttentionState: () => dispatch(actions.home.clearDeleteAttentionState()),
});

export default connect(null, mapDispatchToProps)(GroupedAttentionsList);
